import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import logger from '../../utils/logger';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useAuth } from '../Auth/Auth';
import { Navigate } from 'react-router-dom';
import MoonLoader from 'react-spinners/MoonLoader';
import BreadCrumb from '../BreadCrumb';
import { UserIcon, CurrencyDollarIcon, ChartBarIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const pages = [
  { name: 'Analytics & Reports', to: '/analytics', current: false },
  { name: 'Agent Performance', to: '/analytics/agents', current: true }
];

const AgentPerformance = () => {
  const { role, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Agent data
  const [totalAgents, setTotalAgents] = useState(0);
  const [totalCommission, setTotalCommission] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [agentPerformance, setAgentPerformance] = useState([]);
  const [topAgents, setTopAgents] = useState([]);
  const [monthlyCommissions, setMonthlyCommissions] = useState([]);
  const [customerDistribution, setCustomerDistribution] = useState([]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  useEffect(() => {
    if (role === 'Service Advisor' || role === 'Admin') {
      fetchAgentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, selectedYear]);

  const fetchAgentData = async () => {
    setIsLoading(true);
    try {
      // Fetch agents, customers, and jobs data
      const [agentsSnapshot, customersSnapshot, jobsSnapshot, invoicesSnapshot] = await Promise.all([
        getDocs(collection(db, 'agents')),
        getDocs(collection(db, 'customers')),
        getDocs(collection(db, 'jobs')),
        getDocs(collection(db, 'newInvoices'))
      ]);

      // Build agent lookup map
      const agentMap = {};
      agentsSnapshot.forEach(doc => {
        const agent = doc.data();
        agentMap[doc.id] = {
          id: doc.id,
          name: agent.name || 'Unknown Agent',
          email: agent.email || '',
          phone: agent.phoneNumber || '',
          commission: 0,
          customers: 0,
          jobs: 0,
          revenue: 0,
          monthlyCommissions: Array(12).fill(0)
        };
      });

      // Count customers per agent
      customersSnapshot.forEach(doc => {
        const customer = doc.data();
        const agentId = customer.agentId;

        if (agentId && agentMap[agentId]) {
          agentMap[agentId].customers += 1;
        }
      });

      // Track jobs per agent and calculate commission from invoices
      const jobToAgentMap = {};
      jobsSnapshot.forEach(doc => {
        const job = doc.data();
        const customerId = job.customerId;

        // Find customer's agent
        const customer = customersSnapshot.docs.find(c => c.id === customerId);
        if (customer) {
          const agentId = customer.data().agentId;
          if (agentId && agentMap[agentId]) {
            agentMap[agentId].jobs += 1;
            jobToAgentMap[job.jobCardNo] = agentId;
          }
        }
      });

      // Calculate commission and revenue from invoices
      let totalCommissionAmount = 0;
      let totalCustomerCount = 0;

      // Build agent commission rates lookup
      const agentCommissionRates = {};
      agentsSnapshot.forEach(doc => {
        const agent = doc.data();
        agentCommissionRates[doc.id] = agent.commission || 0; // Commission percentage
      });

      invoicesSnapshot.forEach(doc => {
        const invoice = doc.data();
        const invoiceDate = invoice.invoiceDate?.toDate?.() || invoice.createdAt?.toDate?.();

        if (invoiceDate && invoiceDate.getFullYear() === selectedYear) {
          const jobCardNo = invoice.jobCardNo;
          const agentId = jobToAgentMap[jobCardNo];

          if (agentId && agentMap[agentId]) {
            const revenue = invoice.receivable || 0;

            // Calculate commission: commission% * gross margin
            // Gross margin = sum of (price - costPrice) * quantity for all items
            let grossMargin = 0;
            if (invoice.cartItems && Array.isArray(invoice.cartItems)) {
              grossMargin = invoice.cartItems.reduce((total, item) => {
                const price = parseFloat(item.price) || 0;
                const costPrice = parseFloat(item.costPrice) || 0;
                const quantity = parseInt(item.quantity) || 0;
                return total + ((price - costPrice) * quantity);
              }, 0);
            }

            const commissionRate = agentCommissionRates[agentId] || 0;
            const commissionAmount = (commissionRate / 100) * grossMargin;
            const month = invoiceDate.getMonth();

            agentMap[agentId].revenue += revenue;
            agentMap[agentId].commission += commissionAmount;
            agentMap[agentId].monthlyCommissions[month] += commissionAmount;
            totalCommissionAmount += commissionAmount;
          }
        }
      });

      // Convert to array and calculate metrics
      const agentsArray = Object.values(agentMap)
        .filter(agent => agent.customers > 0 || agent.jobs > 0 || agent.commission > 0)
        .map(agent => ({
          ...agent,
          avgCommissionPerJob: agent.jobs > 0 ? agent.commission / agent.jobs : 0,
          avgRevenuePerCustomer: agent.customers > 0 ? agent.revenue / agent.customers : 0
        }))
        .sort((a, b) => b.commission - a.commission);

      totalCustomerCount = customersSnapshot.size;

      // Set state
      setTotalAgents(agentsArray.length);
      setTotalCommission(totalCommissionAmount);
      setTotalCustomers(totalCustomerCount);
      setAgentPerformance(agentsArray);

      // Top 10 agents by commission
      const top10 = agentsArray.slice(0, 10);
      setTopAgents(top10);

      // Prepare monthly commission data for top 5 agents
      const top5 = agentsArray.slice(0, 5);
      const monthlyData = {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: top5.map((agent, index) => {
          const colors = [
            'rgb(99, 102, 241)',
            'rgb(16, 185, 129)',
            'rgb(249, 115, 22)',
            'rgb(236, 72, 153)',
            'rgb(139, 92, 246)'
          ];

          return {
            label: agent.name,
            data: agent.monthlyCommissions,
            borderColor: colors[index],
            backgroundColor: colors[index],
            tension: 0.4
          };
        })
      };
      setMonthlyCommissions(monthlyData);

      // Customer distribution among agents
      const customerDist = agentsArray.slice(0, 10).map(agent => ({
        name: agent.name,
        customers: agent.customers
      }));
      setCustomerDistribution(customerDist);

    } catch (error) {
      logger.error('Error fetching agent data:', error);
      toast.error('Failed to load agent performance data');
    } finally {
      setIsLoading(false);
    }
  };

  // Chart configurations
  const topAgentsChartData = {
    labels: topAgents.map(a => a.name.length > 20 ? a.name.substring(0, 20) + '...' : a.name),
    datasets: [{
      label: 'Total Commission',
      data: topAgents.map(a => a.commission),
      backgroundColor: 'rgba(139, 92, 246, 0.8)',
      borderColor: 'rgb(139, 92, 246)',
      borderWidth: 1
    }]
  };

  const topAgentsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          afterLabel: function(context) {
            const agent = topAgents[context.dataIndex];
            return [
              `Customers: ${agent.customers}`,
              `Jobs: ${agent.jobs}`,
              `Revenue: ${formatCurrency(agent.revenue)}`,
              `Avg/Job: ${formatCurrency(agent.avgCommissionPerJob)}`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { display: false },
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      },
      y: { grid: { display: false } }
    }
  };

  const monthlyCommissionsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 15, usePointStyle: true }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
          }
        }
      }
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      }
    }
  };

  const customerDistributionData = {
    labels: customerDistribution.map(c => c.name),
    datasets: [{
      data: customerDistribution.map(c => c.customers),
      backgroundColor: [
        'rgba(99, 102, 241, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(249, 115, 22, 0.8)',
        'rgba(236, 72, 153, 0.8)',
        'rgba(139, 92, 246, 0.8)',
        'rgba(59, 130, 246, 0.8)',
        'rgba(245, 158, 11, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(20, 184, 166, 0.8)',
        'rgba(168, 85, 247, 0.8)'
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const customerDistributionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: { padding: 10, usePointStyle: true }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
            return `${context.label}: ${context.parsed} (${percentage}%)`;
          }
        }
      }
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-fuchsia-50">
        <div className="text-center">
          <MoonLoader size={60} color="#D946EF" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (role !== 'Service Advisor' && role !== 'Admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-fuchsia-50 to-pink-50 py-4">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-fuchsia-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg">
                <UserIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Agent Performance</h1>
                <p className="text-sm text-gray-600 mt-1">Commission tracking and agent activity analysis</p>
              </div>
            </div>

            {/* Year Selector */}
            <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-xl shadow-md">
              <label className="text-sm font-medium text-gray-700">Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent text-sm font-medium"
              >
                {[...Array(5)].map((_, i) => {
                  const year = new Date().getFullYear() - i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <MoonLoader size={50} color="#D946EF" />
              <p className="mt-4 text-sm font-medium text-gray-600">Loading agent performance data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Total Agents */}
              <div className="bg-gradient-to-br from-fuchsia-500 to-pink-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-fuchsia-100 text-sm font-medium">Active Agents</p>
                    <h3 className="text-3xl font-bold mt-1">{totalAgents}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <UserIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-fuchsia-100 text-xs">With customers or commissions</p>
              </div>

              {/* Total Commission */}
              <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-purple-100 text-sm font-medium">Total Commission</p>
                    <h3 className="text-2xl font-bold mt-1">{formatCurrency(totalCommission)}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <CurrencyDollarIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-purple-100 text-xs">Paid in {selectedYear}</p>
              </div>

              {/* Total Customers */}
              <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Total Customers</p>
                    <h3 className="text-3xl font-bold mt-1">{totalCustomers}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <ChartBarIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-blue-100 text-xs">Managed by agents</p>
              </div>

              {/* Avg Commission */}
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-orange-100 text-sm font-medium">Avg Commission</p>
                    <h3 className="text-2xl font-bold mt-1">
                      {totalAgents > 0 ? formatCurrency(totalCommission / totalAgents) : formatCurrency(0)}
                    </h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <TrophyIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-orange-100 text-xs">Per agent</p>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Top 10 Agents */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Top 10 Agents by Commission</h3>
                  <p className="text-sm text-gray-600 mt-1">Highest earning agents in {selectedYear}</p>
                </div>
                <div style={{ height: '400px' }}>
                  {topAgents.length > 0 ? (
                    <Bar data={topAgentsChartData} options={topAgentsChartOptions} />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <p>No agent data available for {selectedYear}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Distribution */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Customer Distribution</h3>
                  <p className="text-sm text-gray-600 mt-1">Customers managed by each agent</p>
                </div>
                <div style={{ height: '400px' }}>
                  {customerDistribution.length > 0 ? (
                    <Doughnut data={customerDistributionData} options={customerDistributionOptions} />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <p>No customer distribution data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Monthly Commission Trends */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900">Monthly Commission Trends</h3>
                <p className="text-sm text-gray-600 mt-1">Commission earned by top 5 agents throughout {selectedYear}</p>
              </div>
              <div style={{ height: '350px' }}>
                {monthlyCommissions.datasets?.length > 0 ? (
                  <Line data={monthlyCommissions} options={monthlyCommissionsOptions} />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <p>No monthly trend data available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Agent Performance Table */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-fuchsia-600 to-pink-600 px-6 py-4">
                <h3 className="text-xl font-bold text-white">All Agents ({agentPerformance.length})</h3>
                <p className="text-fuchsia-100 text-sm mt-1">Complete performance breakdown</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent Name</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Customers</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Jobs</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg/Job</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {agentPerformance.length > 0 ? (
                      agentPerformance.map((agent, index) => (
                        <tr key={agent.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                          <td className="px-6 py-4 text-sm">
                            <div>
                              <div className="font-medium text-gray-900">{agent.name}</div>
                              {agent.email && <div className="text-xs text-gray-500">{agent.email}</div>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                            {agent.customers}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                            {agent.jobs}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                            {formatCurrency(agent.revenue)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                            <span className="px-2 py-1 text-xs font-bold rounded-full bg-purple-100 text-purple-700">
                              {formatCurrency(agent.commission)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                            {formatCurrency(agent.avgCommissionPerJob)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center">
                            <UserIcon className="w-12 h-12 text-gray-300 mb-3" />
                            <p className="text-sm">No agent data available for {selectedYear}</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AgentPerformance;
